import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { useI18n } from '../../i18n'
import { formatTimestamp } from '../../lib/youtube/timeFormat'
import { clampAnchors } from '../../lib/validation/segment'
import styles from './Timeline.module.css'

type Props = {
  duration: number | null
  currentTime: number
  startSeconds: number
  endSeconds: number
  onChangeAnchors: (start: number, end: number) => void
  onSeekPlayhead: (seconds: number) => void
  disabled?: boolean
}

type DragTarget = 'start' | 'end' | null

function pct(value: number, duration: number): number {
  if (!duration || duration <= 0) return 0
  return Math.max(0, Math.min(100, (value / duration) * 100))
}

export function Timeline({
  duration,
  currentTime,
  startSeconds,
  endSeconds,
  onChangeAnchors,
  onSeekPlayhead,
  disabled = false,
}: Props) {
  const { t } = useI18n()
  const trackRef = useRef<HTMLDivElement>(null)
  const [dragging, setDragging] = useState<DragTarget>(null)
  const labelId = useId()
  const knownDuration = duration && duration > 0 ? duration : null
  const fallbackDuration = Math.max(endSeconds + 5, startSeconds + 10, currentTime + 5, 60)
  const span = knownDuration ?? fallbackDuration

  const secondsFromClientX = useCallback(
    (clientX: number) => {
      const el = trackRef.current
      if (!el) return 0
      const rect = el.getBoundingClientRect()
      const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
      return ratio * span
    },
    [span],
  )

  useEffect(() => {
    if (!dragging || disabled) return

    const onMove = (event: PointerEvent) => {
      const next = secondsFromClientX(event.clientX)
      if (dragging === 'start') {
        const clamped = clampAnchors(startSeconds, endSeconds, 'start', next, knownDuration)
        onChangeAnchors(clamped.start, clamped.end)
        onSeekPlayhead(clamped.start)
      } else if (dragging === 'end') {
        const clamped = clampAnchors(startSeconds, endSeconds, 'end', next, knownDuration)
        onChangeAnchors(clamped.start, clamped.end)
        onSeekPlayhead(clamped.end)
      }
    }

    const onUp = () => setDragging(null)

    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
    }
  }, [
    dragging,
    disabled,
    startSeconds,
    endSeconds,
    knownDuration,
    span,
    onChangeAnchors,
    onSeekPlayhead,
    secondsFromClientX,
  ])

  const nudgeAnchor = (which: 'start' | 'end', delta: number) => {
    if (disabled) return
    const base = which === 'start' ? startSeconds : endSeconds
    const clamped = clampAnchors(startSeconds, endSeconds, which, base + delta, knownDuration)
    onChangeAnchors(clamped.start, clamped.end)
    onSeekPlayhead(which === 'start' ? clamped.start : clamped.end)
  }

  const startPct = pct(startSeconds, span)
  const endPct = pct(endSeconds, span)
  const playPct = pct(currentTime, span)
  // When Start/End sit on top of each other, put Start above so it can be dragged
  // left (common after a previous technique ends at the video duration).
  const handlesStacked = Math.abs(endPct - startPct) < 1.5
  const startZ = dragging === 'start' ? 4 : handlesStacked || dragging !== 'end' ? 3 : 2
  const endZ = dragging === 'end' ? 4 : handlesStacked ? 2 : 3

  return (
    <div className={styles.root}>
      <div className={styles.labels} id={labelId}>
        <span>{t('timeline.start', { time: formatTimestamp(startSeconds) })}</span>
        <span className={styles.now} aria-live="polite">
          {t('timeline.now', { time: formatTimestamp(currentTime) })}
        </span>
        <span>{t('timeline.end', { time: formatTimestamp(endSeconds) })}</span>
        <span className={styles.muted}>
          {knownDuration
            ? t('timeline.duration', { time: formatTimestamp(knownDuration) })
            : t('timeline.durationPending')}
        </span>
      </div>

      <div
        ref={trackRef}
        className={styles.track}
        role="group"
        aria-labelledby={labelId}
        onPointerDown={(event) => {
          if (disabled) return
          // Click on empty track seeks playhead only (does not mutate anchors)
          if ((event.target as HTMLElement).dataset.handle) return
          const next = secondsFromClientX(event.clientX)
          onSeekPlayhead(Math.max(0, Math.min(next, span)))
        }}
      >
        <div
          className={styles.range}
          style={{ left: `${startPct}%`, width: `${Math.max(0, endPct - startPct)}%` }}
        />

        <button
          type="button"
          className={`${styles.handle} ${styles.start}`}
          style={{ left: `${startPct}%`, zIndex: startZ }}
          data-handle="start"
          aria-label={t('timeline.startHandle', { time: formatTimestamp(startSeconds) })}
          disabled={disabled}
          onPointerDown={(e) => {
            e.preventDefault()
            e.stopPropagation()
            ;(e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId)
            setDragging('start')
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowLeft') {
              e.preventDefault()
              nudgeAnchor('start', e.shiftKey ? -5 : -1)
            } else if (e.key === 'ArrowRight') {
              e.preventDefault()
              nudgeAnchor('start', e.shiftKey ? 5 : 1)
            }
          }}
        >
          <span className={styles.handleArrow} aria-hidden="true" />
          <span className={styles.handleStem} aria-hidden="true" />
          <span className={styles.handleLabel} aria-hidden="true">
            S
          </span>
        </button>

        <button
          type="button"
          className={`${styles.handle} ${styles.end}`}
          style={{ left: `${endPct}%`, zIndex: endZ }}
          data-handle="end"
          aria-label={t('timeline.endHandle', { time: formatTimestamp(endSeconds) })}
          disabled={disabled}
          onPointerDown={(e) => {
            e.preventDefault()
            e.stopPropagation()
            ;(e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId)
            setDragging('end')
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowLeft') {
              e.preventDefault()
              nudgeAnchor('end', e.shiftKey ? -5 : -1)
            } else if (e.key === 'ArrowRight') {
              e.preventDefault()
              nudgeAnchor('end', e.shiftKey ? 5 : 1)
            }
          }}
        >
          <span className={styles.handleStem} aria-hidden="true" />
          <span className={styles.handleArrow} aria-hidden="true" />
          <span className={styles.handleLabel} aria-hidden="true">
            E
          </span>
        </button>

        <div
          className={styles.playhead}
          style={{ left: `${playPct}%` }}
          aria-hidden="true"
        />
      </div>
    </div>
  )
}
